import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useIsAuthenticated } from './useAuth';

interface Props {
  children: ReactNode;
}

/**
 * Route guard: renders its children only with a session, otherwise sends the
 * user to the login screen.
 *
 * A client-side guard is a navigation convenience, not a security boundary —
 * the API rejects every unauthenticated request on its own. What this buys is
 * not rendering a dashboard shell that would immediately fill with 401s.
 */
export default function RequireAuth({ children }: Props) {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  if (!isAuthenticated) {
    // `from` lets the login screen return the user where they were headed.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
